import React from "react";
import { motion } from "framer-motion";
import {
  CreditCard,
  CheckCircle,
  RefreshCw,
  Shield,
  Zap,
  ArrowRight,
  Receipt,
  Database,
  Smartphone,
  Wifi,
} from "lucide-react";

const CardReadersSlide = () => {
  return (
    <div className="h-full w-full bg-slate-900 flex items-center justify-center p-12 relative overflow-hidden text-white">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-emerald-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />

      <div className="flex flex-row h-full w-full max-w-[1400px] gap-12 relative z-10 items-center">
        {/* Left Side: Content */}
        <div className="w-1/2 flex flex-col justify-center space-y-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-sm font-semibold w-fit">
            <CreditCard className="w-4 h-4" />
            <span>Card Readers</span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
            In-Office Payments,{" "}
            <span className="text-emerald-400">Automatically Reconciled.</span>
          </h2>

          <p className="text-lg text-slate-300 leading-relaxed max-w-xl">
            Physical card readers that connect directly to Asha's payment engine
            and Asha PMS. Every swipe, tap, or insert is automatically posted
            and reconciled — no double entry, no missed payments.
          </p>

          <div className="space-y-4 pt-2">
            <div className="flex items-start gap-4">
              <div className="p-2.5 bg-emerald-500/20 rounded-xl text-emerald-400 mt-0.5">
                <RefreshCw className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Auto-Reconciliation</h3>
                <p className="text-slate-400 text-sm">
                  Payments are automatically matched to patient ledgers and
                  posted in Asha PMS. Zero manual reconciliation.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="p-2.5 bg-blue-500/20 rounded-xl text-blue-400 mt-0.5">
                <Database className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">
                  Native Asha Payments
                </h3>
                <p className="text-slate-400 text-sm">
                  Card readers post directly into Asha PMS. One source of truth
                  for all payments.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="p-2.5 bg-purple-500/20 rounded-xl text-purple-400 mt-0.5">
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">
                  EMV Chip, Tap & Swipe
                </h3>
                <p className="text-slate-400 text-sm">
                  Accepts all payment methods — chip cards, contactless (Apple
                  Pay, Google Pay), and magnetic stripe.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Card Reader Visual + Flow */}
        <div className="w-1/2 flex items-center justify-center">
          <motion.div
            initial={{ x: 40, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="w-full max-w-lg"
          >
            {/* Main Card with terminal visual */}
            <div className="relative">
              {/* Card Reader Device */}
              <div className="bg-slate-800/80 backdrop-blur-xl rounded-3xl border border-slate-700 p-8 shadow-2xl">
                {/* Terminal Screen */}
                <div className="bg-slate-950 rounded-2xl p-6 mb-6 border border-slate-800">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                      <span className="text-xs font-semibold text-slate-400">
                        Asha Pay Terminal
                      </span>
                    </div>
                    <Wifi className="w-4 h-4 text-emerald-500" />
                  </div>

                  <div className="text-center space-y-3">
                    <motion.div
                      animate={{ scale: [1, 1.02, 1] }}
                      transition={{ duration: 3, repeat: Infinity }}
                    >
                      <p className="text-slate-500 text-xs uppercase tracking-wider font-bold">
                        Amount Due
                      </p>
                      <p className="text-4xl font-bold text-white mt-1">
                        $245.00
                      </p>
                    </motion.div>
                    <div className="flex items-center justify-center gap-2 mt-2">
                      <div className="h-px flex-1 bg-slate-800" />
                      <span className="text-xs text-slate-500 px-2">
                        Patient: Sarah Miller
                      </span>
                      <div className="h-px flex-1 bg-slate-800" />
                    </div>
                  </div>

                  {/* Tap/Insert Animation */}
                  <motion.div
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="mt-6 flex flex-col items-center"
                  >
                    <div className="w-16 h-16 rounded-full border-2 border-dashed border-emerald-500/50 flex items-center justify-center">
                      <Smartphone className="w-7 h-7 text-emerald-400" />
                    </div>
                    <p className="text-emerald-400 text-xs font-semibold mt-2">
                      Tap, Insert, or Swipe
                    </p>
                  </motion.div>
                </div>

                {/* Payment Methods Accepted */}
                <div className="flex items-center justify-center gap-3">
                  {["Visa", "MC", "Amex", "Apple Pay"].map((method, i) => (
                    <div
                      key={i}
                      className="px-3 py-1.5 rounded-lg bg-slate-700/50 border border-slate-600/50 text-[10px] font-bold text-slate-300 uppercase tracking-wider"
                    >
                      {method}
                    </div>
                  ))}
                </div>
              </div>

              {/* Flow Arrows showing auto-reconciliation */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-6 space-y-3"
              >
                {/* Step 1 */}
                <div className="flex items-center gap-3 bg-slate-800/60 rounded-xl p-3 border border-slate-700/50">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white">
                      Patient pays at front desk
                    </p>
                    <p className="text-xs text-slate-500">
                      Card tapped on terminal
                    </p>
                  </div>
                  <motion.div
                    animate={{ x: [0, 4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ArrowRight className="w-4 h-4 text-emerald-500" />
                  </motion.div>
                </div>

                {/* Step 2 */}
                <div className="flex items-center gap-3 bg-slate-800/60 rounded-xl p-3 border border-slate-700/50">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <Receipt className="w-4 h-4 text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white">
                      Auto-posted to patient ledger
                    </p>
                    <p className="text-xs text-slate-500">
                      Saved in Asha PMS in real-time
                    </p>
                  </div>
                  <motion.div
                    animate={{ x: [0, 4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
                  >
                    <ArrowRight className="w-4 h-4 text-blue-500" />
                  </motion.div>
                </div>

                {/* Step 3 */}
                <div className="flex items-center gap-3 bg-emerald-500/10 rounded-xl p-3 border border-emerald-500/20">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-emerald-300">
                      Fully reconciled — zero manual work
                    </p>
                    <p className="text-xs text-emerald-400/60">
                      Deposit matches, reports generated
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CardReadersSlide;
